import { z } from 'zod';
import {
  emailSchema,
  nameSchema,
  optionalStringSchema,
  phoneSchema,
  priceSchema,
  quantitySchema,
} from '../../validation';
import {
  itemPackingSchema,
  itemTypeSchema,
  temperatureSchema,
  vehicleTypesSchema,
  weightSchema,
} from '../../common';
import { orderPODCopySettingSchema } from '../order/order.settings.model';
import { orgWaybillSettingsSchema } from './org.waybill-settings.model';
import { languageSchema } from '../../common/language.schema';

const orgContactPersonSchema = z.object({
  /** the email of the contact. */
  email: emailSchema.optional(),
  /** the phone number of the contact if possible */
  phone: phoneSchema.optional(),
  /** the name of the contact if possible */
  name: nameSchema.optional(),
});

/**
 * Unified Org settings — superset of `businessSettingsSchema` and the
 * settings carried by the legacy `Carrier` collection.
 *
 * Org-level preferences for default item / vehicle behavior, financial
 * + operational reporting recipients, and POD copy settings. Mirrors
 * the existing Business + Carrier settings shapes so the dual-write
 * transition is a copy-over.
 */
export const orgSettingsSchema = z.object({
  // ==== ITEMS SETTINGS ====

  /** the default item name that will be used in order. */
  item_default_name: optionalStringSchema(nameSchema).optional(),

  /** the default item type that will be used in order. */
  item_default_type: itemTypeSchema.optional(),

  /** the default cost of the item that will be used in order */
  item_default_cost: priceSchema.optional(),

  /** the default item weight in TONs. */
  item_default_weight: weightSchema.optional(),

  /** the default packing type (pallet, package, other). */
  item_default_packing: itemPackingSchema.optional(),

  /** the default quantity of items per vehicle. */
  item_default_quantity: quantitySchema.optional(),

  /** the default temperature preference for chilled/frozen vehicles. */
  item_default_temperature: temperatureSchema.optional(),

  // ==== Vehicle SETTINGS ====

  /** the default vehicle type that will be used in order.
   *
   * By default, if we have to choose one,,, we will always choose the first element in the array.
   */
  vehicle_preferred_types: z.array(vehicleTypesSchema).optional(),

  /** the proof of delivery (POD) settings. */
  pod_settings: orderPODCopySettingSchema.optional(),

  // ==== Contacts ====

  /** the contacts that will be used to send financial reports to such as invoices. */
  contacts_financial_reports: z.array(orgContactPersonSchema).optional(),

  /** the contacts that will be used to send operation reports to such as orders. */
  contacts_operation_reports: z.array(orgContactPersonSchema).optional(),

  // ==== Invoicing ====

  /**
   * Org-level default for "auto-capture invoices on delivery". Used
   * when neither the Client nor the Account has its own setting
   * (see `client-settings.schema.ts` for the full resolution chain
   * inside `OrderEventsService.onDelivered`).
   *
   * Defaults to `false` — Orgs opt in explicitly.
   */
  allow_auto_invoice_capture: z.boolean().optional(),

  // ==== AI inbox ====

  /**
   * Language staff read AI voice-note summaries in, for threads on this
   * org's lines. Absent → `ar`.
   */
  ai_summary_language: languageSchema.optional(),

  // ==== Waybill ====

  /** Carrier-branded HTML waybill settings (accent, logo, Hero footer). */
  waybill_settings: orgWaybillSettingsSchema.optional(),
});
export type OrgSettings = z.infer<typeof orgSettingsSchema>;

/** PATCH body for `Org.settings` — `waybill_settings: null` clears custom waybill branding. */
export const orgProfileSettingsUpdateSchema = orgSettingsSchema.partial().extend({
  waybill_settings: orgWaybillSettingsSchema.nullable().optional(),
});
